import { apiGet, apiPost } from './apiClient';

export interface LowEnrollmentLesson {
  lesson: import('@court-sync/shared').Lesson;
  occurrences: Array<{
    date: string;
    enrollment: number;
    minimumRequired: number;
  }>;
}

export interface LessonOverride {
  id: string;
  lessonId: string;
  lessonName: string;
  originalDate: string;
  overrideType: 'cancelled' | 'postponed';
  reason?: string;
  newDate?: string;
  newStartTime?: string;
  newEndTime?: string;
  newCourtId?: string;
  createdAt: string;
  notifiedAt?: string;
}

export const lessonOverrideService = {
  async getLowEnrollmentLessons(academyId: string): Promise<LowEnrollmentLesson[]> {
    const response = await apiGet<LowEnrollmentLesson[]>('/lesson-overrides/low-enrollment', {
      params: { academyId },
    });
    return response.data || [];
  },

  async removeSession(
    lessonId: string,
    date: string,
    academyId: string
  ): Promise<{ message: string }> {
    const response = await apiPost('/lesson-overrides/remove-session', {
      lessonId,
      originalDate: date,
      academyId,
    });
    const body = response as { message?: string };
    return { message: body.message ?? 'Session removed' };
  },

  async cancelLesson(
    lessonId: string,
    date: string,
    reason: string,
    notifyStudents: boolean = true,
    academyId?: string,
    grantMakeupCredit: boolean = false
  ): Promise<{
    overrideId: string;
    message: string;
    notificationsSent: number;
  }> {
    const response = await apiPost('/lesson-overrides/cancel', {
      lessonId,
      originalDate: date,
      reason,
      notifyStudents,
      academyId,
      grantMakeupCredit,
    });
    const body = response as {
      data?: { id?: string };
      message?: string;
    };
    return {
      overrideId: body.data?.id ?? '',
      message: body.message ?? '',
      notificationsSent: 0,
    };
  },

  async postponeLesson(
    lessonId: string,
    originalDate: string,
    newDate: string,
    newStartTime: string,
    newEndTime: string,
    reason: string,
    notifyStudents: boolean = true,
    newCourtId?: string,
    academyId?: string,
    notifySms?: boolean
  ): Promise<{
    overrideId: string;
    message: string;
    notificationsSent: number;
  }> {
    const response = await apiPost('/lesson-overrides/postpone', {
      lessonId,
      originalDate,
      newDate,
      newStartTime,
      newEndTime,
      reason,
      notifyStudents,
      newCourtId,
      academyId,
      ...(notifySms !== undefined ? { notifySms } : {}),
    });
    const body = response as {
      data?: { replacementSession?: { id?: string }; originalSession?: { id?: string } };
      message?: string;
    };
    return {
      overrideId: body.data?.replacementSession?.id ?? body.data?.originalSession?.id ?? '',
      message: body.message ?? '',
      notificationsSent: 0,
    };
  },

  async getOverrides(filters?: {
    academyId?: string;
    lessonId?: string;
    overrideType?: 'cancelled' | 'postponed';
    fromDate?: string;
    toDate?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<LessonOverride[]> {
    const params: Record<string, string> = {};
    if (filters?.academyId) params.academyId = filters.academyId;
    if (filters?.lessonId) params.lessonId = filters.lessonId;
    if (filters?.overrideType) params.overrideType = filters.overrideType;
    const fromDate = filters?.fromDate ?? filters?.dateFrom;
    if (fromDate) params.fromDate = fromDate;
    const toDate = filters?.toDate ?? filters?.dateTo;
    if (toDate) params.toDate = toDate;
    const response = await apiGet<LessonOverride[]>('/lesson-overrides', { params });
    return response.data || [];
  },

  async getOverridesForLesson(lessonId: string, academyId: string): Promise<LessonOverride[]> {
    const response = await apiGet<LessonOverride[]>('/lesson-overrides', {
      params: { lessonId, academyId },
    });
    return response.data || [];
  },

  async getOverride(overrideId: string): Promise<LessonOverride> {
    const response = await apiGet<LessonOverride>(`/lesson-overrides/${overrideId}`);
    return response.data!;
  },

  async getTermCancellations(academyId: string): Promise<
    Array<{
      id: string;
      lessonId: string;
      academyId: string;
      termId: string;
      cancelledFromDate: string;
      reason: string;
      createdBy: string;
    }>
  > {
    const response = await apiGet<Array<{
      id: string;
      lessonId: string;
      academyId: string;
      termId: string;
      cancelledFromDate: string;
      reason: string;
      createdBy: string;
    }>>('/lesson-overrides/term-cancellations', {
      params: { academyId },
    });
    return response.data || [];
  },

  async cancelTermLesson(
    lessonId: string,
    academyId: string,
    reason: string,
    fromDate?: string,
    notifyStudents: boolean = true,
    grantMakeupCredit: boolean = false
  ): Promise<{
    cancellationCreated: boolean;
    bookingsCancelled: number;
    refundsProcessed: number;
    notificationsSent: number;
  }> {
    const response = await apiPost<{
      cancellationCreated: boolean;
      bookingsCancelled: number;
      refundsProcessed: number;
      notificationsSent: number;
    }>('/lesson-overrides/cancel-term', {
      lessonId,
      academyId,
      reason,
      fromDate,
      notifyStudents,
      grantMakeupCredit,
    });
    return response.data!;
  },

  async replaceSessionCoaches(body: {
    academyId: string;
    sessionId: string;
    coachIds: string[];
    reason?: string;
    notifyCoachEmail?: boolean;
  }): Promise<unknown> {
    const response = await apiPost('/lesson-overrides/replace-coaches', body);
    if (!response?.success) {
      throw new Error(response?.error ?? 'Failed to update session coaches');
    }
    return response.data;
  },

  async rescheduleSession(body: {
    academyId: string;
    sessionId: string;
    startTime: string;
    endTime: string;
    courtIds: string[];
    sourceCourtId?: string;
    reason?: string;
    notifyGuardians?: boolean;
    notifySms?: boolean;
  }): Promise<unknown> {
    const response = await apiPost('/lesson-overrides/reschedule-session', body);
    if (!response?.success) {
      throw new Error(response?.error ?? 'Failed to reschedule session');
    }
    return response.data;
  },

  async previewBulkReplaceCoaches(params: {
    academyId: string;
    fromDate: string;
    toDate: string;
    fromCoachId: string;
    toCoachId: string;
    lessonIds?: string[];
    lessonType?: 'group' | 'private' | 'camp';
  }): Promise<
    Array<{
      sessionId: string;
      lessonId: string;
      lessonName: string;
      sessionDate: string;
      startTime: string;
      endTime: string;
      currentCoachIds: string[];
    }>
  > {
    const response = await apiGet<Array<{
      sessionId: string;
      lessonId: string;
      lessonName: string;
      sessionDate: string;
      startTime: string;
      endTime: string;
      currentCoachIds: string[];
    }>>('/lesson-overrides/replace-coaches/bulk/preview', {
      params: {
        academyId: params.academyId,
        fromDate: params.fromDate,
        toDate: params.toDate,
        fromCoachId: params.fromCoachId,
        toCoachId: params.toCoachId,
        ...(params.lessonIds?.length ? { lessonIds: params.lessonIds.join(',') } : {}),
        ...(params.lessonType ? { lessonType: params.lessonType } : {}),
      },
    });
    return response.data ?? [];
  },

  async bulkReplaceSessionCoaches(body: {
    academyId: string;
    fromDate: string;
    toDate: string;
    fromCoachId: string;
    toCoachId: string;
    lessonIds?: string[];
    lessonType?: 'group' | 'private' | 'camp';
    reason?: string;
  }): Promise<{ updatedCount: number; sessionIds: string[] }> {
    const response = await apiPost<{ updatedCount: number; sessionIds: string[] }>(
      '/lesson-overrides/replace-coaches/bulk',
      body
    );
    if (!response?.success) {
      throw new Error(response?.error ?? 'Bulk replace failed');
    }
    return response.data!;
  },
};

export default lessonOverrideService;
